import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../config';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText,
  IonIcon,
  IonButton,
  IonSpinner
} from '@ionic/react';
import { 
  arrowBackOutline, 
  calculatorOutline,
  heartOutline,
  informationCircleOutline,
  personOutline,
  keyOutline
} from 'ionicons/icons';
import PreventCalculator from './PreventCalculator';

const PreventCalculatorTestHarness = () => {
  const history = useHistory();
  const [auth_token, setAuthToken] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [error, setError] = useState(null);

  // Load members when auth token is provided
  const loadMembers = useCallback(async () => {
    if (!auth_token.trim()) return;
    
    setLoadingMembers(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${config.apiUrl}/members_names`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Sort members by Surname, First_name
      const sortedMembers = response.data.sort((a, b) => {
        const surnameA = a.Surname || '';
        const surnameB = b.Surname || '';
        if (surnameA !== surnameB) {
          return surnameA.localeCompare(surnameB);
        }
        const firstNameA = a.First_name || '';
        const firstNameB = b.First_name || '';
        return firstNameA.localeCompare(firstNameB);
      });
      
      setMembers(sortedMembers);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members. Please check your auth token.');
    } finally {
      setLoadingMembers(false);
    }
  }, [auth_token]);

  // Handle member selection
  const handleMemberSelect = (memberId) => {
    setSelectedMember(memberId);
    if (memberId) {
      setShowCalculator(true);
    }
  };

  // Load members when auth token changes
  useEffect(() => {
    if (auth_token.trim()) {
      loadMembers();
    } else {
      setMembers([]);
      setSelectedMember('');
      setShowCalculator(false);
    }
  }, [auth_token, loadMembers]);

  if (showCalculator && selectedMember) {
    return (
      <PreventCalculator 
        member_id={selectedMember} 
        auth_token={auth_token}
        onBack={() => setShowCalculator(false)}
      />
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={() => history.goBack()}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>PREVENT Calculator Test Harness</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={calculatorOutline} />
                PREVENT Risk Calculator Test Harness
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>This test harness allows you to test the PREVENT Risk Calculator with real member data.</p>
                <p>Enter your auth token and select a member to auto-populate health metrics.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Authentication</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonIcon icon={keyOutline} slot="start" />
                <IonLabel position="stacked">Auth Token *</IonLabel>
                <IonInput
                  value={auth_token}
                  onIonInput={e => setAuthToken(e.detail.value)}
                  placeholder="Enter your auth token"
                  type="text"
                  clearInput
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {error && (
            <IonCard color="danger">
              <IonCardContent>
                <IonText color="light">
                  <IonIcon icon={informationCircleOutline} />
                  {error}
                </IonText>
              </IonCardContent>
            </IonCard>
          )}

          {auth_token && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Select Member</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {loadingMembers ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IonSpinner name="crescent" />
                    <IonText>Loading members...</IonText>
                  </div>
                ) : (
                  <IonItem>
                    <IonIcon icon={personOutline} slot="start" />
                    <IonLabel position="stacked">Member *</IonLabel>
                    <IonSelect
                      value={selectedMember}
                      onIonChange={e => handleMemberSelect(e.detail.value)}
                      placeholder="Select a member"
                      interface="popover"
                    >
                      {members.map(member => (
                        <IonSelectOption key={member.id} value={member.id.toString()}>
                          {member.Surname}, {member.First_name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                )}
              </IonCardContent>
            </IonCard>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <IonButton 
              expand="block" 
              onClick={() => history.push('/prevent')}
              className="launch-button"
            >
              <IonIcon icon={heartOutline} slot="start" />
              Launch PREVENT Calculator (Manual Mode)
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PreventCalculatorTestHarness;
